import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNotification } from "./../notification.hook";
import { RefreshReactQuery } from "../../constants/AppConstants";
import {
  DATA_UPDATED_SUCCESS_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
} from "../../constants/Dictionary";
import CmSheetInternalGradeService from "../../services/api/cmSheet/CmSheetInternalGrade.service";
import { UpdateCollaboratorCmSheetInternalGradeRequest } from "../../types/api/collaborators.type";
import { useEffect } from "react";
import { CM_SHEET_STATE_MACHINE_QUERY_KEY } from "./stateMachine.hook";

const CM_SHEET_INTERNAL_GRADE_QUERY_KEY = "get-cm-sheet-internal-grade";

export const useCmSheetInternalGrade = (cmSheetId: number, enabled = true) => {
  const { notification } = useNotification();
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [CM_SHEET_INTERNAL_GRADE_QUERY_KEY, cmSheetId],
    queryFn: () => CmSheetInternalGradeService.get(cmSheetId),
    staleTime: RefreshReactQuery.MODERATE,
    enabled: enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useUpdateCmSheetInternalGrade = (cmSheetId: number) => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const { mutateAsync: updateInternalGrade, isPending: isUpdating } =
    useMutation({
      mutationFn: (body: UpdateCollaboratorCmSheetInternalGradeRequest) =>
        CmSheetInternalGradeService.update(cmSheetId, body),
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [CM_SHEET_INTERNAL_GRADE_QUERY_KEY],
          }),
          queryClient.invalidateQueries({
            queryKey: [CM_SHEET_STATE_MACHINE_QUERY_KEY],
          }),
        ]);
        notification.success(DATA_UPDATED_SUCCESS_LABEL, undefined, "toast");
      },
      onError: (error) => {
        console.error(error);
        notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
      },
    });

  return { updateInternalGrade, isUpdating };
};
